import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card, Btn, Badge, EmptyState, AInput, ATextarea, ASelect } from "../AdminUI";
import { Megaphone, Download, Copy, Trash2, Sparkles } from "lucide-react";

const SERVICES = ["Commercial Soft-Wash", "Paver Sealing", "High-Pressure Washing", "Driveway & Concrete Cleaning", "Pool Area Cleaning"];
const FORMATS = ["Square post (1:1)", "Vertical post (4:5)", "Story (9:16)", "Landscape (16:9)", "Facebook feed ad", "Instagram feed ad", "Organic social post", "Video ad script"];

export default function AdMaker() {
  const [brief, setBrief] = useState({ idea: "", property_type: "residential", services: [], platforms: ["Facebook", "Instagram"], format: FORMATS[0], mention_financing: false, notes: "" });
  const [variations, setVariations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const loadC = () => api.get("/admin/admaker/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  useEffect(() => { loadC(); }, []);

  const toggleService = (s) => setBrief((b) => ({ ...b, services: b.services.includes(s) ? b.services.filter((x) => x !== s) : [...b.services, s] }));
  const togglePlatform = (p) => setBrief((b) => ({ ...b, platforms: b.platforms.includes(p) ? b.platforms.filter((x) => x !== p) : [...b.platforms, p] }));

  const generate = async () => {
    if (!brief.idea.trim()) { toast.error("Enter a campaign idea"); return; }
    setLoading(true); setVariations([]);
    try {
      const r = await api.post("/admin/admaker/generate", brief);
      setVariations(r.data.variations || []);
      if (!r.data.variations?.length) toast.error("No variations returned, try again");
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setLoading(false); }
  };

  const save = async (status) => {
    try {
      await api.post("/admin/admaker/campaigns", { idea: brief.idea, brief, variations, status });
      toast.success(status === "approved" ? "Campaign approved" : "Saved as draft"); loadC();
    } catch { toast.error("Failed"); }
  };
  const delC = async (id) => { if (!window.confirm("Delete campaign?")) return; await api.delete(`/admin/admaker/campaigns/${id}`); loadC(); };

  const download = (v, i) => {
    const text = `HEADLINE: ${v.headline}\n\nPRIMARY TEXT:\n${v.primary_text}\n\nDESCRIPTION: ${v.description}\nCALL TO ACTION: ${v.call_to_action}\nHASHTAGS: ${(v.hashtags || []).join(" ")}\nAUDIENCE: ${v.suggested_audience}\nOBJECTIVE: ${v.suggested_objective}\nBUDGET (suggestion): ${v.suggested_budget_range}\n${v.video_script ? `\nVIDEO SCRIPT:\n${v.video_script}` : ""}`;
    const blob = new Blob([text], { type: "text/plain" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `ad-variation-${i + 1}.txt`; a.click();
  };
  const editVar = (i, k, val) => setVariations((vs) => vs.map((v, idx) => idx === i ? { ...v, [k]: val } : v));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-head font-extrabold text-3xl text-[var(--navy)] uppercase tracking-tight">Ad Maker</h1>
        <p className="text-gray-500 text-sm">Generate editable Facebook & Instagram ad concepts. Nothing is ever auto-published or auto-spent.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-4">
        <Card title="Campaign Brief">
          <div className="space-y-4">
            <ATextarea label="Campaign Idea" value={brief.idea} rows={3} onChange={(e) => setBrief({ ...brief, idea: e.target.value })} placeholder="e.g. Promote driveway cleaning before Labor Day" data-testid="admaker-idea" />
            <ASelect label="Audience" value={brief.property_type} options={["residential", "commercial"]} onChange={(e) => setBrief({ ...brief, property_type: e.target.value })} />
            <div><p className="text-xs font-semibold text-gray-500 uppercase mb-1.5">Services</p>
              <div className="flex flex-wrap gap-2">{SERVICES.map((s) => <button key={s} onClick={() => toggleService(s)} className={`text-xs px-2.5 py-1.5 rounded-full ${brief.services.includes(s) ? "bg-[var(--action)] text-white" : "bg-gray-100 text-gray-600"}`}>{s}</button>)}</div></div>
            <div><p className="text-xs font-semibold text-gray-500 uppercase mb-1.5">Platforms</p>
              <div className="flex gap-2">{["Facebook", "Instagram"].map((p) => <button key={p} onClick={() => togglePlatform(p)} className={`text-xs px-3 py-1.5 rounded-full ${brief.platforms.includes(p) ? "bg-[var(--navy)] text-white" : "bg-gray-100 text-gray-600"}`}>{p}</button>)}</div></div>
            <ASelect label="Ad Format" value={brief.format} options={FORMATS} onChange={(e) => setBrief({ ...brief, format: e.target.value })} />
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={brief.mention_financing} onChange={(e) => setBrief({ ...brief, mention_financing: e.target.checked })} className="w-4 h-4 accent-[var(--action)]" /> Mention EasyQuickFund financing</label>
            <Btn onClick={generate} loading={loading} className="w-full" data-testid="admaker-generate"><Sparkles className="w-4 h-4" /> Generate Ad Concepts</Btn>
          </div>
        </Card>

        <div className="space-y-4">
          {variations.length === 0 ? <Card><EmptyState icon={Megaphone} title="No concepts yet" hint="Fill the brief and generate ad variations." /></Card> : (
            <>
              {variations.map((v, i) => (
                <Card key={i} title={`Variation ${i + 1}`} action={<Btn variant="ghost" onClick={() => download(v, i)}><Download className="w-4 h-4" /></Btn>}>
                  <div className="space-y-2">
                    <AInput label="Headline" value={v.headline || ""} onChange={(e) => editVar(i, "headline", e.target.value)} />
                    <ATextarea label="Primary Text" rows={3} value={v.primary_text || ""} onChange={(e) => editVar(i, "primary_text", e.target.value)} />
                    <div className="grid grid-cols-2 gap-2">
                      <AInput label="Description" value={v.description || ""} onChange={(e) => editVar(i, "description", e.target.value)} />
                      <AInput label="Call to Action" value={v.call_to_action || ""} onChange={(e) => editVar(i, "call_to_action", e.target.value)} />
                    </div>
                    <div className="flex flex-wrap gap-1">{(v.hashtags || []).map((h) => <span key={h} className="text-[11px] bg-gray-100 px-2 py-0.5 rounded-full text-gray-600">{h}</span>)}</div>
                    <div className="text-xs text-gray-500 grid grid-cols-2 gap-1">
                      {v.suggested_audience && <p><b>Audience:</b> {v.suggested_audience}</p>}
                      {v.suggested_objective && <p><b>Objective:</b> {v.suggested_objective}</p>}
                      {v.suggested_budget_range && <p className="col-span-2"><b>Budget (suggestion only):</b> {v.suggested_budget_range}</p>}
                    </div>
                    {v.video_script && <ATextarea label="Video Script" rows={3} value={v.video_script} onChange={(e) => editVar(i, "video_script", e.target.value)} />}
                  </div>
                </Card>
              ))}
              <div className="flex gap-2"><Btn variant="ghost" onClick={() => save("draft")}>Save Draft</Btn><Btn variant="green" onClick={() => save("approved")} data-testid="approve-campaign">Approve Campaign</Btn></div>
            </>
          )}
        </div>
      </div>

      <Card title="Saved Campaigns">
        {campaigns.length === 0 ? <EmptyState icon={Megaphone} title="No saved campaigns" /> : (
          <div className="divide-y divide-gray-100">
            {campaigns.map((c) => (
              <div key={c.id} className="flex items-center justify-between py-3">
                <div><p className="font-semibold text-[var(--navy)]">{c.idea}</p><p className="text-xs text-gray-400">{c.variations?.length || 0} variations · {new Date(c.created_at).toLocaleDateString()}</p></div>
                <div className="flex items-center gap-2"><Badge color={c.status === "approved" ? "green" : "amber"}>{c.status}</Badge>
                  <Btn variant="ghost" onClick={() => { setBrief(c.brief || brief); setVariations(c.variations || []); toast.info("Loaded into editor"); }}><Copy className="w-4 h-4" /></Btn>
                  <Btn variant="danger" onClick={() => delC(c.id)}><Trash2 className="w-4 h-4" /></Btn></div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
